import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  MessageSquare, Send, Bot, Smartphone, ExternalLink, Pause, Play, Inbox as InboxIcon,
  User, Users, Clock, CheckCircle2, RotateCcw, StickyNote, Tag as TagIcon, ArrowRightLeft, Hand, Building2, Briefcase, ListTodo, FileText, X, ArrowLeft,
} from "lucide-react";

const CHANNEL = {
  site_ia: { label: "Site · IA", Icon: Bot, cls: "text-[#0CDBF2] bg-[#0CDBF2]/10 border-[#0CDBF2]/30" },
  whatsapp: { label: "WhatsApp", Icon: Smartphone, cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" },
};
const STATUS_LABEL = {
  ia: "Automação/IA", aguardando_atendimento: "Aguardando atendimento", em_atendimento: "Em atendimento",
  aguardando_cliente: "Aguardando cliente", adiada: "Adiada", encerrada: "Encerrada",
};
const STATUS_CLS = {
  ia: "text-[#0CDBF2] bg-[#0CDBF2]/10 border-[#0CDBF2]/30",
  aguardando_atendimento: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  em_atendimento: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
  aguardando_cliente: "text-sky-300 bg-sky-400/10 border-sky-400/30",
  adiada: "text-slate-300 bg-slate-400/10 border-slate-500/30",
  encerrada: "text-slate-400 bg-slate-700/20 border-slate-700",
};
const STAGE_LABEL = { novo_lead: "Novo Lead", contato: "Contato", orcamento: "Orçamento", negociacao: "Negociação", fechado: "Fechado", perdido: "Perdido" };

const BRL = (v) => "R$ " + (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

export default function Conversas() {
  const [convs, setConvs] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [box, setBox] = useState("entrada");
  const [entradaFilter, setEntradaFilter] = useState("todas");
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [events, setEvents] = useState([]);
  const [noteMode, setNoteMode] = useState(false);
  const [tagDefs, setTagDefs] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [tagOpen, setTagOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadConvs = useCallback(() => api.get("/conversations").then(({ data }) => setConvs(data)), []);
  useEffect(() => { loadConvs(); api.get("/auth/me").then(({ data }) => setMe(data)); api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); api.get("/tags").then(({ data }) => setTagDefs(data)).catch(() => {}); }, [loadConvs]);
  useEffect(() => { const t = setInterval(loadConvs, 8000); return () => clearInterval(t); }, [loadConvs]);

  const loadDetail = useCallback((c) => {
    api.get(`/conversations/${c.id}/messages`).then(({ data }) => setMsgs(data));
    api.get(`/conversations/${c.id}/context`).then(({ data }) => setCtx(data)).catch(() => setCtx(null));
    api.get(`/conversations/${c.id}/events`).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);
  useEffect(() => { if (sel) loadDetail(sel); }, [sel, loadDetail]);
  useEffect(() => {
    const cid = searchParams.get("conv");
    if (cid && convs.length) { const f = convs.find((c) => c.id === cid); if (f) { setSel(f); setSearchParams({}, { replace: true }); } }
  }, [convs, searchParams, setSearchParams]);
  useEffect(() => { if (sel) { const f = convs.find((c) => c.id === sel.id); if (f) setSel(f); } }, [convs]); // keep selection fresh

  const inBox = (c, b) => {
    const st = c.status;
    if (b === "todas") return true;
    if (b === "entrada") return st === "ia" || st === "aguardando_atendimento";
    if (b === "minhas") return c.assignee_id === me?.id && st !== "encerrada";
    if (b === "atendimento") return st === "em_atendimento";
    if (b === "aguardando_cliente") return st === "aguardando_cliente";
    if (b === "adiadas") return st === "adiada";
    if (b === "encerradas") return st === "encerrada";
    return true;
  };
  const entradaOk = (c) => {
    if (box !== "entrada" || entradaFilter === "todas") return true;
    if (entradaFilter === "ia") return c.status === "ia";
    if (entradaFilter === "humano") return c.status === "aguardando_atendimento";
    return true;
  };
  const list = convs.filter((c) => inBox(c, box) && entradaOk(c));
  const countBox = (b) => convs.filter((c) => inBox(c, b)).length;

  const boxes = [
    { key: "entrada", label: "Entrada", Icon: InboxIcon },
    { key: "minhas", label: "Minhas conversas", Icon: User },
    { key: "atendimento", label: "Em atendimento", Icon: Hand },
    { key: "aguardando_cliente", label: "Aguardando cliente", Icon: Clock },
    { key: "adiadas", label: "Adiadas", Icon: Pause },
    { key: "encerradas", label: "Encerradas", Icon: CheckCircle2 },
    { key: "todas", label: "Todas", Icon: MessageSquare },
  ];

  const act = async (path, body) => {
    try { await api.post(`/conversations/${sel.id}/${path}`, body || {}); await loadConvs(); loadDetail(sel); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha na ação"); }
  };
  const send = async () => {
    if (!text.trim() || !sel) return;
    try {
      if (noteMode) { await api.post(`/conversations/${sel.id}/note`, { text }); toast.success("Nota interna adicionada"); }
      else { await api.post(`/conversations/${sel.id}/messages`, { text }); }
      setText(""); loadDetail(sel); loadConvs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao enviar"); }
  };
  const toggleTag = async (name, action) => { await api.post(`/conversations/${sel.id}/tags`, { name, action }); loadConvs(); loadDetail(sel); };
  const applyTag = (name, applied) => toggleTag(name, applied ? "remove" : "add");
  const createAndApply = async () => { const n = newTag.trim(); if (!n) return; await toggleTag(n, "add"); setNewTag(""); };
  const transfer = async () => { const u = prompt("ID/e-mail? Escolha pelo nome:\n" + users.map((x) => `${x.name}`).join(", ")); if (!u) return; const t = users.find((x) => x.name.toLowerCase() === u.toLowerCase() || x.email === u); if (!t) return toast.error("Usuário não encontrado"); act("transfer", { user_id: t.id }); };
  const snooze = async () => { act("snooze", { until: "" }); };

  const ch = (c) => CHANNEL[c.channel] || { label: c.channel, Icon: MessageSquare, cls: "text-slate-400 bg-slate-400/10 border-slate-700" };
  const iaPaused = sel && sel.ai_active === false;

  return (
    <div className="h-full flex" data-testid="inbox">
      {/* LEFT: boxes + list */}
      <aside className={`${sel ? "hidden md:flex" : "flex"} w-full md:w-80 border-r border-[#1E293B] bg-[#0B0F17] flex-col shrink-0`}>
        <div className="p-3 border-b border-[#1E293B]">
          <h2 className="font-heading font-semibold px-1 mb-2">Conversas</h2>
          <div className="space-y-0.5">
            {boxes.map((b) => (
              <button key={b.key} onClick={() => setBox(b.key)} data-testid={`box-${b.key}`}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm ${box === b.key ? "bg-[#0CDBF2]/10 text-[#0CDBF2]" : "text-slate-300 hover:bg-white/[0.03]"}`}>
                <b.Icon size={14} /> <span className="flex-1 text-left">{b.label}</span>
                <span className="text-[11px] text-slate-500">{countBox(b.key)}</span>
              </button>
            ))}
          </div>
          {box === "entrada" && (
            <div className="flex gap-1 mt-2">
              {[["todas", "Todas"], ["ia", "IA ativa"], ["humano", "Aguardando humano"]].map(([k, l]) => (
                <button key={k} onClick={() => setEntradaFilter(k)} data-testid={`entrada-filter-${k}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${entradaFilter === k ? "border-[#0CDBF2] text-[#0CDBF2]" : "border-slate-700 text-slate-400"}`}>{l}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {list.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">Nenhuma conversa nesta caixa.</div>}
          {list.map((c) => {
            const { Icon, cls } = ch(c);
            return (
              <button key={c.id} onClick={() => setSel(c)} data-testid={`conv-${c.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-800/60 hover:bg-white/[0.02] ${sel?.id === c.id ? "bg-[#0CDBF2]/5 border-l-2 border-l-[#0CDBF2]" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium truncate">{c.customer_name}</div>
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${cls}`}><Icon size={10} /></span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || "—"}</div>
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${STATUS_CLS[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                  {c.tags?.slice(0, 2).map((t) => <span key={t.name} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300">{t.name}</span>)}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* CENTER: messages */}
      <main className={`${sel ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0`}>
        {!sel ? (
          <div className="flex-1 flex items-center justify-center text-slate-500">Selecione uma conversa</div>
        ) : (
          <>
            <div className="border-b border-[#1E293B] px-5 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <button onClick={() => setSel(null)} className="md:hidden -ml-1 mr-0.5 p-1 rounded-md text-slate-300 hover:bg-white/5" data-testid="conv-back"><ArrowLeft size={18} /></button>
                  <div className="font-medium truncate" data-testid="conv-header-name">{sel.customer_name}</div>
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border ${ch(sel).cls}`}>{ch(sel).label}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_CLS[sel.status]}`} data-testid="conv-status">{STATUS_LABEL[sel.status]}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap text-[11px]">
                <ActBtn testid="conv-assume" onClick={() => act("assume")} Icon={Hand}>Assumir</ActBtn>
                <ActBtn testid="conv-transfer" onClick={transfer} Icon={ArrowRightLeft}>Transferir</ActBtn>
                {iaPaused
                  ? <ActBtn testid="conv-resume-ia" onClick={() => act("resume-ia")} Icon={Play} cls="border-[#0CDBF2]/40 text-[#0CDBF2]">Reativar IA</ActBtn>
                  : <ActBtn testid="conv-pause-ia" onClick={() => act("pause-ia")} Icon={Pause} cls="border-amber-500/40 text-amber-300">Pausar IA</ActBtn>}
                <ActBtn testid="conv-snooze" onClick={snooze} Icon={Clock}>Adiar</ActBtn>
                {sel.status === "encerrada"
                  ? <ActBtn testid="conv-reopen" onClick={() => act("reopen")} Icon={RotateCcw}>Reabrir</ActBtn>
                  : <ActBtn testid="conv-close" onClick={() => act("close")} Icon={CheckCircle2}>Encerrar</ActBtn>}
                <Popover open={tagOpen} onOpenChange={setTagOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="conv-tag" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-700 text-slate-300 hover:bg-white/[0.04]"><TagIcon size={11} /> Tag</button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-60 bg-[#0B0F17] border-slate-800 text-slate-200 p-2">
                    <div className="text-xs text-slate-500 mb-1.5">Aplicar tag</div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {tagDefs.map((td) => {
                        const applied = (sel?.tags || []).some((t) => t.name === td.name && t.type === "manual");
                        return (
                          <button key={td.id} onClick={() => applyTag(td.name, applied)} data-testid={`tagpick-${td.name}`}
                            className="text-[10px] px-2 py-0.5 rounded-full border" style={{ borderColor: td.color, color: td.color, opacity: applied ? 1 : 0.55 }}>{td.name}</button>
                        );
                      })}
                      {!tagDefs.length && <span className="text-[11px] text-slate-600">Nenhuma tag definida. Crie em Configurações → Tags.</span>}
                    </div>
                    <div className="flex gap-1">
                      <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createAndApply()} placeholder="Nova tag" className="bg-[#07090E] border-slate-800 h-7 text-xs" data-testid="conv-tag-input" />
                      <Button onClick={createAndApply} size="sm" className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] h-7 px-2" data-testid="conv-tag-apply">+</Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {msgs.map((m) => (
                m.direction === "note" ? (
                  <div key={m.id} className="max-w-md mx-auto bg-amber-400/10 border border-amber-400/30 text-amber-100 px-3 py-2 rounded-lg text-xs" data-testid={`note-${m.id}`}>
                    <StickyNote size={11} className="inline mr-1" />Nota · {m.author}: {m.text}
                  </div>
                ) : (
                  <div key={m.id} className={`max-w-md px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${m.direction === "out" ? "bg-[#0CDBF2]/10 border border-[#0CDBF2]/30 ml-auto" : "bg-[#111622] border border-[#1E293B]"}`} data-testid={`msg-${m.id}`}>
                    {m.image_id && <img src={`${API_URL}/mockups/${m.image_id}`} alt="Mockup" className="rounded-md mb-2 max-w-xs border border-[#1E293B]" />}
                    {m.media_id && <img src={`${API_URL}/media/${m.media_id}`} alt="Imagem" className="rounded-md mb-2 max-w-xs border border-[#1E293B]" />}
                    {m.text}
                    <div className="text-[10px] text-slate-500 mt-1">{m.author} · {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                )
              ))}
              {!msgs.length && <div className="text-slate-500 text-sm text-center py-10">Sem mensagens ainda.</div>}
            </div>
            <div className="p-3 border-t border-[#1E293B]">
              <div className="flex gap-1 mb-2 text-[11px]">
                <button onClick={() => setNoteMode(false)} className={`px-2 py-0.5 rounded ${!noteMode ? "bg-[#0CDBF2]/15 text-[#0CDBF2]" : "text-slate-400"}`} data-testid="mode-reply">Responder</button>
                <button onClick={() => setNoteMode(true)} className={`px-2 py-0.5 rounded ${noteMode ? "bg-amber-400/15 text-amber-300" : "text-slate-400"}`} data-testid="mode-note">Nota interna</button>
              </div>
              <div className="flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={noteMode ? "Nota interna (o cliente não vê)..." : (sel.channel === "site_ia" ? "Responder (chat do site)..." : "Responder pelo WhatsApp (pausa a IA)...")}
                  className="bg-[#07090E] border-slate-800" data-testid="conv-input" />
                <Button onClick={send} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E]" data-testid="conv-send"><Send size={16} /></Button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* RIGHT: context */}
      {sel && (
        <aside className="hidden lg:block w-80 border-l border-[#1E293B] bg-[#0B0F17] overflow-y-auto p-4 space-y-4 text-sm" data-testid="conv-context">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Cliente</div>
            <div className="font-medium flex items-center gap-2"><User size={13} className="text-[#0CDBF2]" />{sel.customer_name}</div>
            {sel.customer_company && <div className="text-slate-400 flex items-center gap-2 mt-1"><Building2 size={12} />{sel.customer_company}</div>}
            {sel.customer_whatsapp && <a href={`https://wa.me/55${sel.customer_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline flex items-center gap-2 mt-1 text-xs" data-testid="conv-wa-link"><Smartphone size={12} />{sel.customer_whatsapp}</a>}
            {ctx?.customer && <Link to={`/app/clientes`} className="text-[#0CDBF2] text-xs hover:underline flex items-center gap-1 mt-1"><ExternalLink size={11} /> Ver cliente</Link>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Responsável" value={sel.assignee_type === "ia" ? "IA" : (sel.assignee_name || "Não atribuído")} />
            <Field label="Equipe" value={ctx?.team || "—"} />
          </div>
          {sel.tags?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Tags</div>
              <div className="flex flex-wrap gap-1">
                {sel.tags.map((t) => (
                  <span key={t.name} className="text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 border-slate-600 text-slate-300 bg-slate-700/30" style={t.type === "manual" ? { borderColor: t.color, color: t.color } : undefined} data-testid={`tag-${t.name}`}>
                    {t.name}
                    {t.type === "manual" && <button onClick={() => toggleTag(t.name, "remove")} className="hover:text-red-400"><X size={9} /></button>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {sel.open_opportunity && (
            <div className="rounded-lg border border-[#0CDBF2]/30 bg-[#0CDBF2]/5 p-3" data-testid="conv-open-opp">
              <div className="text-[11px] text-[#0CDBF2] mb-1 flex items-center gap-1"><Briefcase size={12} /> Oportunidade aberta</div>
              <div className="font-medium">{sel.open_opportunity.title}</div>
              <div className="text-xs text-slate-400 mt-1">{STAGE_LABEL[sel.open_opportunity.stage] || sel.open_opportunity.stage} · {BRL(sel.open_opportunity.value)}</div>
              <Link to="/app/funil" className="text-[#0CDBF2] text-xs hover:underline flex items-center gap-1 mt-1"><ExternalLink size={11} /> Ver no funil</Link>
            </div>
          )}
          <ContextList title="Oportunidades" Icon={Briefcase} items={ctx?.opportunities} render={(o) => `${o.title} · ${STAGE_LABEL[o.stage_key] || o.stage_key} · ${BRL(o.estimated_value)}`} link="/app/funil" />
          <ContextList title="Tarefas" Icon={ListTodo} items={ctx?.tasks} render={(t) => `${t.title} · ${t.status}${t.due_date ? " · " + t.due_date : ""}`} link="/app/tarefas" />
          <ContextList title="Orçamentos" Icon={FileText} items={ctx?.quotes} render={(q) => `${q.number || "—"} · ${BRL(q.total)} · ${q.status}`} link="/app/orcamentos" />
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Histórico</div>
            <div className="space-y-1.5" data-testid="conv-events">
              {(events || []).slice(0, 12).map((e) => (
                <div key={e.id} className="text-[11px] text-slate-400 flex gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#0CDBF2] mt-1.5 shrink-0" />
                  <div><span className="text-slate-300">{e.text}</span> <span className="text-slate-600">· {new Date(e.created_at).toLocaleString("pt-BR")}</span></div>
                </div>
              ))}
              {!events?.length && <div className="text-slate-600 text-xs">Sem eventos.</div>}
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}

function ActBtn({ children, onClick, Icon, cls = "border-slate-700 text-slate-300", testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-white/[0.04] ${cls}`}>
      <Icon size={11} /> {children}
    </button>
  );
}
function Field({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-slate-200 text-xs mt-0.5 truncate">{value}</div></div>;
}
function ContextList({ title, Icon, items, render, link }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Icon size={12} /> {title}</div>
      <div className="space-y-1">
        {items.slice(0, 6).map((it) => (
          <Link key={it.id} to={link} className="block text-xs text-slate-300 hover:text-[#0CDBF2] truncate border-b border-slate-800/50 pb-1">{render(it)}</Link>
        ))}
      </div>
    </div>
  );
}
